import { seoConfig } from "./seoConfig.js";
import { resolveSiteUrl } from "./resolveUrl.js";

// Builders de datos estructurados JSON-LD (schema.org). Cada función devuelve
// un objeto plano listo para inyectarse con useJsonLd/upsertJsonLd.

// La API del catálogo devuelve varios campos de texto con padding de ancho
// fijo (espacios finales). Se usa de forma uniforme en metadata y JSON-LD.
export function cleanText(value) {
  return value == null ? "" : String(value).trim();
}

export function buildOrganization() {
  const org = seoConfig.organization;
  return {
    "@context": "https://schema.org",
    "@type": "AutoDealer",
    name: org.name,
    legalName: org.legalName,
    url: resolveSiteUrl("/"),
    logo: resolveSiteUrl(org.logoPath),
    image: resolveSiteUrl(org.logoPath),
    telephone: org.telephone,
    email: org.email,
    ...(org.sameAs?.length ? { sameAs: org.sameAs } : {}),
  };
}

export function buildWebSite() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: seoConfig.siteName,
    url: resolveSiteUrl("/"),
    inLanguage: seoConfig.language,
  };
}

/**
 * @param {Array<{name: string, path?: string}>} items - En orden desde Inicio.
 *        El último elemento (página actual) puede omitir `path`.
 */
export function buildBreadcrumbList(items = []) {
  if (!items.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      ...(item.path ? { item: resolveSiteUrl(item.path) } : {}),
    })),
  };
}

/**
 * Ficha de vehículo (CarDataSheet.vue). Usa el objeto `Catalogo` devuelto por
 * la API y la URL absoluta de la foto principal ya resuelta por la vista.
 * Todos los campos son opcionales/seguros ante datos faltantes.
 */
export function buildVehicle({ car, path, imageUrl, empresa } = {}) {
  if (!car || !car.idInv) return null;

  const name = [car.marca, car.modelo, car.version, car.anioModelo]
    .map(cleanText)
    .filter(Boolean)
    .join(" ");
  if (!name) return null;

  const price = Number(car.precioContado) > 0 ? Number(car.precioContado) : null;

  return {
    "@context": "https://schema.org",
    "@type": "Vehicle",
    name,
    brand: cleanText(car.marca) ? { "@type": "Brand", name: cleanText(car.marca) } : undefined,
    model: cleanText(car.modelo) || undefined,
    // "modelDate" es la propiedad vigente en schema.org; "vehicleModelDate"
    // está marcada supersededBy y no se emite para evitar redundancia.
    modelDate: car.anioModelo ? String(car.anioModelo) : undefined,
    mileageFromOdometer:
      car.kilometraje != null
        ? { "@type": "QuantitativeValue", value: Number(car.kilometraje), unitCode: "KMT" }
        : undefined,
    vehicleTransmission: cleanText(car.transmision) || undefined,
    url: path ? resolveSiteUrl(path) : undefined,
    image: imageUrl ? resolveSiteUrl(imageUrl) : undefined,
    itemCondition: "https://schema.org/UsedCondition",
    offers: price
      ? {
          "@type": "Offer",
          priceCurrency: "MXN",
          price,
          availability: "https://schema.org/InStock",
          itemCondition: "https://schema.org/UsedCondition",
          seller: {
            "@type": "AutoDealer",
            name: cleanText(car.empresa) || cleanText(empresa?.empresa) || seoConfig.organization.name,
          },
        }
      : undefined,
  };
}

export function buildContactPage() {
  const org = seoConfig.organization;
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contacto CAR4U",
    url: resolveSiteUrl("/contacto"),
    about: {
      "@type": "AutoDealer",
      name: org.name,
      telephone: org.telephone,
      email: org.email,
    },
  };
}

export default {
  buildOrganization,
  buildWebSite,
  buildBreadcrumbList,
  buildVehicle,
  buildContactPage,
  cleanText,
};
